import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Slide, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import Cookies from "universal-cookie";
import arrivalValidityRuleAPI from "../../Network/ArrivalValidityRule";
import geofenceAPI from "../../Network/Geofence";

const cookies = new Cookies();

/* design tokens (UI_UX_AGENT_STANDARDS §F.2, matching the arrival-validity page) */
const t = {
  surface: "#FFFFFF", surfaceMuted: "#F1F5F9", border: "#CBD5E1", borderStrong: "#94A3B8",
  text: "#0F172A", textMuted: "#64748B", primary: "#1C64F2", primarySoft: "rgba(28,100,242,0.10)",
  success: "#10B981", danger: "#EF4444",
};

const authParams = () => ({
  username: cookies.get("user_name") || null,
  user_id: cookies.get("user_id") || null,
  account_name: cookies.get("account_name") || null,
  account_id: cookies.get("account_id") || null,
  sid: cookies.get("sid") || null,
  baseurl: cookies.get("baseUrl") || null,
  id: cookies.get("id") || null,
});

/* 24h allowed-arrival timeline — identical visual to the arrival-validity page. */
function TimelineBar({ rule, tt }) {
  const closed = parseInt(rule.is_closed) === 1;
  const toMin = (s) => { if (!s) return null; const p = String(s).split(":"); return (parseInt(p[0]) || 0) * 60 + (parseInt(p[1]) || 0); };
  const hhmm = (s) => (s ? String(s).substring(0, 5) : "");
  const pct = (m) => `${(m / 1440) * 100}%`;
  const fromM = toMin(rule.allowed_from);
  const toM = toMin(rule.allowed_to);
  const hasWindow = fromM != null && toM != null;

  const segs = [];
  if (!closed) {
    if (hasWindow) {
      if (fromM <= toM) segs.push([fromM, toM]);
      else { segs.push([fromM, 1440]); segs.push([0, toM]); } // overnight window
    } else {
      segs.push([0, 1440]); // no time restriction
    }
  }

  const acceptedText = hasWindow
    ? `${tt("ARRIVAL_VALIDITY_RULES.accepted")} (${hhmm(rule.allowed_from)} - ${hhmm(rule.allowed_to)})`
    : tt("ARRIVAL_VALIDITY_RULES.accepted_allday");

  const ticks = [{ m: 0, label: "00:00", sub: tt("ARRIVAL_VALIDITY_RULES.midnight") }];
  if (!closed && hasWindow) {
    ticks.push({ m: fromM, label: hhmm(rule.allowed_from) });
    ticks.push({ m: toM, label: hhmm(rule.allowed_to) });
  }
  ticks.push({ m: 1440, label: "24:00", sub: tt("ARRIVAL_VALIDITY_RULES.midnight") });

  return (
    <Box sx={{ mt: 3 }}>
      {/* tick labels */}
      <Box dir="ltr" sx={{ position: "relative", height: 30 }}>
        {ticks.map((tk, i) => {
          const atStart = tk.m === 0, atEnd = tk.m === 1440;
          return (
            <Box key={i} sx={{ position: "absolute", left: pct(tk.m), transform: atStart ? "none" : atEnd ? "translateX(-100%)" : "translateX(-50%)" }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium", lineHeight: 1.1, whiteSpace: "nowrap", textAlign: "center" }}>{tk.label}</Typography>
              {tk.sub ? <Typography sx={{ fontSize: 10, color: t.textMuted, fontFamily: "Cairo", whiteSpace: "nowrap", textAlign: "center" }}>{tk.sub}</Typography> : null}
            </Box>
          );
        })}
      </Box>

      {/* bar */}
      <Box dir="ltr" sx={{ position: "relative", height: 46, borderRadius: 1.5, overflow: "hidden", bgcolor: t.danger, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {closed ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, zIndex: 1 }}>
            <BlockIcon sx={{ color: "#fff", fontSize: 20 }} />
            <Typography sx={{ color: "#fff", fontFamily: "Cairo-Bold", fontSize: 14, textAlign: "center" }}>{tt("ARRIVAL_VALIDITY_RULES.closed_full")}</Typography>
          </Box>
        ) : segs.map((s, i) => (
          <Box key={i} sx={{ position: "absolute", top: 0, bottom: 0, left: pct(s[0]), width: pct(s[1] - s[0]), bgcolor: t.success }} />
        ))}
      </Box>

      {/* legend */}
      {!closed ? (
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
          <Typography sx={{ fontSize: 12, color: t.success, fontFamily: "Cairo-Medium" }}>● {acceptedText}</Typography>
          <Typography sx={{ fontSize: 12, color: t.danger, fontFamily: "Cairo-Medium" }}>● {tt("ARRIVAL_VALIDITY_RULES.rejected_outside")}</Typography>
        </Box>
      ) : null}
    </Box>
  );
}

// Popup listing the allowed-arrival durations (as 24h bars) for the rules that
// apply to the CURRENT transactions filter window — scoped by destination and
// the arrived-at date range, external scope only (this is the external page).
export default function ArrivalDurationsModal({ open, onClose, filters }) {
  const [tt] = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);
  const [destinations, setDestinations] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      const [rulesRes, destRes] = await Promise.all([
        arrivalValidityRuleAPI.arrivalValidityRules({ params: { page: 1, page_size: 200, enabled: 1 } }),
        geofenceAPI.getGeofenceList({ params: { ...authParams(), port_type_ids: [2] } }),
      ]);
      if (!active) return;
      const list = rulesRes?.data?.data?.data;
      setRules(Array.isArray(list) ? list : []);
      const draw = destRes?.data?.data;
      setDestinations(Array.isArray(draw) ? draw : (draw?.data || []));
      setLoading(false);
    })();
    return () => { active = false; };
  }, [open]);

  if (!open) return null;

  const destId = filters?.destination_geofence_id ? String(filters.destination_geofence_id) : "";

  // Resolve the date window the user actually filtered by. Prefer the arrival-date
  // filter (most relevant to arrival validity); otherwise fall back to the
  // creation-date filter ("الفترة"). The matching "all_time*" flag means that
  // dimension isn't constrained, so it doesn't define a window.
  const day = (v) => (v ? String(v).substring(0, 10) : null);
  const arrivedActive = Number(filters?.all_time_arrived_at) !== 1 && (filters?.arrived_at_from || filters?.arrived_at_to);
  const creationActive = Number(filters?.all_time) !== 1 && (filters?.creation_date_from || filters?.creation_date_to);
  let winFrom = null, winTo = null;
  if (arrivedActive) { winFrom = day(filters?.arrived_at_from); winTo = day(filters?.arrived_at_to); }
  else if (creationActive) { winFrom = day(filters?.creation_date_from); winTo = day(filters?.creation_date_to); }

  const dateOverlaps = (rule) => {
    if (!winFrom && !winTo) return true;
    const ef = rule.effective_from ? String(rule.effective_from).substring(0, 10) : null;
    const et = rule.effective_to ? String(rule.effective_to).substring(0, 10) : null;
    if (winTo && ef && ef > winTo) return false;
    if (winFrom && et && et < winFrom) return false;
    return true;
  };

  const matched = (rules || []).filter((r) => {
    if (Number(r.enabled) === 0) return false;
    const scope = String(r.transaction_scope || "").toLowerCase();
    if (scope !== "external" && scope !== "any") return false;
    if (destId) {
      const rd = r.destination_geofence_id ? String(r.destination_geofence_id) : "";
      if (rd && rd !== destId) return false; // rule for another destination
    }
    return dateOverlaps(r);
  });

  // Newest first: order by effective_from (fallback effective_to) descending;
  // open-dated rules ("all dates") sink to the bottom.
  const sortKey = (r) => day(r.effective_from) || day(r.effective_to) || "";
  matched.sort((a, b) => sortKey(b).localeCompare(sortKey(a)));

  const destLabel = (id) => {
    if (!id) return tt("ARRIVAL_VALIDITY_RULES.any_destination");
    const d = destinations.find((x) => String(x.id) === String(id));
    return d ? d.name : `#${id}`;
  };
  const effRange = (r) => {
    const f = r.effective_from ? r.effective_from.substring(0, 10) : null;
    const to = r.effective_to ? r.effective_to.substring(0, 10) : null;
    if (f && to) return `${f} → ${to}`;
    if (f) return `${f} → …`;
    if (to) return `… → ${to}`;
    return tt("ARRIVAL_VALIDITY_RULES.all_dates");
  };

  return (
    <Box
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      sx={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", p: 2 }}
    >
      <Slide direction="down" in timeout={220}>
        <Box sx={{ bgcolor: t.surface, borderRadius: 3, border: `1px solid ${t.border}`, boxShadow: "0 20px 55px rgba(0,0,0,0.28)", width: "100%", maxWidth: 720, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
          {/* header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: `1px solid ${t.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: t.primarySoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ScheduleIcon sx={{ color: t.primary, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 16, color: t.text }}>{tt("ARRIVAL_VALIDITY_RULES.durations_title")}</Typography>
                <Typography sx={{ fontFamily: "Cairo", fontSize: 12, color: t.textMuted }}>{tt("ARRIVAL_VALIDITY_RULES.durations_hint")}</Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ color: t.textMuted, "&:hover": { bgcolor: t.surfaceMuted, color: t.text } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* body */}
          <Box sx={{ p: 3, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ textAlign: "center", py: 6 }}><CircularProgress size={28} sx={{ color: t.primary }} /></Box>
            ) : matched.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <ScheduleIcon sx={{ fontSize: 44, color: t.borderStrong, mb: 1 }} />
                <Typography sx={{ fontFamily: "Cairo-Medium", color: t.textMuted }}>{tt("ARRIVAL_VALIDITY_RULES.durations_none")}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {matched.map((r) => (
                  <Box key={r.id} sx={{ bgcolor: t.surface, border: `1px solid ${t.border}`, borderRadius: 3, p: 2.5 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 15, color: t.text }}>{r.name || tt("ARRIVAL_VALIDITY_RULES.untitled")}</Typography>
                        <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: 0.4, borderRadius: 999, fontSize: 12, fontWeight: 700, fontFamily: "Cairo-Medium", bgcolor: t.surfaceMuted, color: t.text }}>{destLabel(r.destination_geofence_id)}</Box>
                      </Box>
                      <Typography sx={{ fontFamily: "Cairo-Medium", fontSize: 13, color: t.textMuted }}>
                        <Box component="span" dir="ltr" sx={{ unicodeBidi: "isolate" }}>{effRange(r)}</Box>
                      </Typography>
                    </Box>
                    <TimelineBar rule={r} tt={tt} />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Slide>
    </Box>
  );
}
